import Slider from "components/DesignSystem/Slider/Slider";
import Tab from "components/DesignSystem/Tab/Tab";
import { ChatPane } from "components/ChatPane/ChatPane";
import { useEffect, useMemo, useState } from "react";
import DashboardContainer from "components/dashboard/DashboardContainer";
import {
  useGetFilingDetailsQuery,
  useGetFilingTransactionsQuery,
} from "store/apis/filing";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { StatusBadge } from "./FilingAccordionTable";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { isOverdue, statusMap } from "./Filing";
import { Button } from "components/DesignSystem/Button/Button";
import { FILED, SUBSCRIBED } from "constants/filing";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { CircleWarning } from "components/icons/CircleWarning";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { getBillingCartUrl } from "util/getBillingCartUrl";
import { useStartFilingSubscription } from "./useStartFilingSubscription";

import { useCurrentGroup } from "hooks/useCurrentGroup";
import { FilingDetails } from "./FilingDetails";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { formatDate } from "utils/formatDate";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { UserGroup } from "components/icons/UserGroup";
import Loader from "components/DesignSystem/Loader/loader";
import Table from "components/DesignSystem/Table/V2/Table";
import { EmptySalesTax } from "components/Illustrations/EmptySalesTax";
import { Invoice } from "types/Models/invoice";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import ToolTip from "components/design/toolTip";
import { useCurrentEntity } from "hooks/useCurrentEntity";

const transactionColumnHelper = createColumnHelper<Invoice>();

const FilingTransactions = ({
  currentFilingId,
}: {
  currentFilingId: string;
}) => {
  const [pageNum, setPageNum] = useState(1);
  const entityId = useCurrentEntityId();

  const {
    data: transactionsData,
    isLoading,
    isSuccess,
  } = useGetFilingTransactionsQuery({
    entityId,
    filingId: currentFilingId,
    pageNum,
  });

  const columns = useMemo(
    () => [
      transactionColumnHelper.accessor("invoice_date", {
        header: "Date",
        cell: (info) => <p className="t-m-0">{formatDate(info.getValue())}</p>,
        size: 25,
      }),
      transactionColumnHelper.accessor("customer", {
        header: "Customer",
        cell: (info) => (
          <div>
            <div className="t-text-body-sm t-text-text-60 t-line-clamp-1">
              {info.row.original.customer?.company_name}
            </div>
            <div className="t-text-body-sm t-text-text-30 t-line-clamp-1">
              {info.row.original.customer?.email || "-"}
            </div>
          </div>
        ),
        size: 20,
      }),
      transactionColumnHelper.accessor("invoice_amount", {
        header: () => <div className="t-text-right">Amount</div>,
        cell: (info) => (
          <div className="t-text-right">
            <AmountSuperScript amount={Number(info.getValue())} />
          </div>
        ),
        size: 20,
      }),
      transactionColumnHelper.accessor("tax_amount", {
        header: () => <div className="t-text-right">Tax collected</div>,
        cell: (info) => (
          <div className="t-text-right">
            <AmountSuperScript amount={Number(info.getValue())} />
          </div>
        ),
        size: 20,
      }),
      transactionColumnHelper.accessor("source", {
        header: "Source",
        cell: (info) => {
          const { logo_url } = info.row.original;

          const customer_name = info.row.original.customer?.company_name;
          return (
            <div className="t-flex t-justify-center">
              {logo_url ? (
                <ToolTip text={customer_name}>
                  <span>
                    <Avatar size="small" src={logo_url} alt={info.getValue()} />
                  </span>
                </ToolTip>
              ) : (
                <ToolTip text="Manual">
                  <span>
                    <UserGroup />
                  </span>
                </ToolTip>
              )}
            </div>
          );
        },
        size: 15,
      }),
    ],
    []
  );

  const table = useReactTable({
    data: transactionsData?.transactions || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <div className="t-flex t-items-center t-justify-center t-h-full">
        <Loader size="small" />
      </div>
    );
  }

  return (
    <div className="t-mt-4">
      <div className="t-flex t-justify-between t-items-center t-mb-4">
        <p className="t-text-subtitle-sm t-text-text-60 t-m-0">Transactions</p>
        <PaginationHandler
          page_num={pageNum}
          per_page={transactionsData?.per_page ?? 0}
          total_count={transactionsData?.total_count ?? 0}
          total_pages={transactionsData?.total_pages ?? 0}
          onPageNumChange={setPageNum}
        />
      </div>
      <Async.Root
        isSuccess={isSuccess}
        isLoading={false}
        isEmpty={!transactionsData?.transactions?.length}
      >
        <Async.Success>
          <Table.Container className="t-h-full">
            <Table.Content>
              <Table.Head className="t-inline-table t-w-full t-bg-neutral-10 !t-border-b">
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.HeadRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Table.HeadCell
                        key={header.id}
                        style={{ width: `${header.getSize()}%` }}
                        className="t-py-4 t-px-3 t-uppercase t-text-subtext-sm"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </Table.HeadCell>
                    ))}
                  </Table.HeadRow>
                ))}
              </Table.Head>
              <Table.Body>
                {table.getRowModel().rows.map((row) => (
                  <Table.Row
                    key={row.id}
                    className="t-inline-table t-w-full hover:t-bg-surface-lighter-grey"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell
                        key={cell.id}
                        className="t-px-3"
                        style={{ width: `${cell.column.getSize()}%` }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Content>
          </Table.Container>
        </Async.Success>
        <Async.Empty>
          <div className="t-flex t-flex-col t-items-center t-w-full t-pt-20">
            <EmptySalesTax />
            <p className="t-m-0 t-text-text-30 t-text-body-sm t-mt-6">
              No transactions added yet
            </p>
          </div>
        </Async.Empty>
      </Async.Root>
    </div>
  );
};

export const FilingSlider = ({
  isOpen,
  onClose,
  selectedFilling,
  onAddTransaction,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedFilling: string;
  onAddTransaction?: () => void;
}) => {
  const [currentTab, setCurrentTab] = useState<"DETAILS" | "CHAT">("DETAILS");
  const entityId = useCurrentEntityId();
  const { secondary_channel_url: generalHelpChannel } = useCurrentGroup();
  const { salestax_channel } = useCurrentEntity();
  const { data, isLoading, isSuccess } = useGetFilingDetailsQuery(
    {
      fillingId: selectedFilling,
      entityId,
    },
    { skip: !selectedFilling }
  );
  const [channelId, setChannelId] = useState<string | null>(
    salestax_channel || generalHelpChannel || null
  );

  useEffect(() => {
    if (data?.task?.channel_url) {
      setChannelId(data.task.channel_url);
    }
  }, [data?.task?.channel_url]);

  const { isAdmin } = useRoleBasedView();
  const { uuid: groupId } = useCurrentGroupContext();
  const { isAddingToCart, startSubscriptionCheckout } =
    useStartFilingSubscription(
      data?.payment_schedule ?? "",
      data?.state ?? "",
      data
        ? {
            filingId: data.uuid,
            startDate: data.filing_period_start_date,
            endDate: data.filing_period_end_date,
          }
        : undefined
    );

  const handleViewStatus = () => {
    if (!data?.task) {
      return;
    }
    window.open(`/tax/filings/${data.task.task_id}`, "_blank");
  };

  const handleViewCart = () => {
    const cartUrl = getBillingCartUrl({ isAdmin, groupId, entityId });
    window.open(cartUrl, "_blank");
  };

  return (
    <Slider.Root open={isOpen} onOpenChange={onClose}>
      <Slider.Content width={690}>
        <Async.Root
          isSuccess={isSuccess}
          isLoading={isLoading}
          isEmpty={isSuccess && !data}
        >
          <Async.Success>
            <Tab.Root
              value={currentTab}
              asChild
              onValueChange={(v) => setCurrentTab(v as typeof currentTab)}
            >
              <DashboardContainer className="t-h-full t-relative">
                <DashboardContainer.Header>
                  <Slider.Header
                    bottom={
                      <Tab.List className="!t-pt-0">
                        <Tab.Trigger value="DETAILS">
                          <span className="t-text-body-sm">Details</span>
                        </Tab.Trigger>
                        <Tab.Trigger value="CHAT">
                          <span className="t-text-body-sm ">Chat</span>
                        </Tab.Trigger>
                      </Tab.List>
                    }
                  >
                    <Slider.Title className="t-gap-x-2 t-items-center t-flex">
                      <span> {data?.state}</span>
                      <StatusBadge status={data?.status || ""}>
                        {statusMap[data?.status as keyof typeof statusMap]}
                      </StatusBadge>
                      {Boolean(
                        data?.filing_due_date && data?.status !== FILED
                      ) &&
                        isOverdue(data?.filing_due_date!) && (
                          <div className="t-flex t-items-center t-gap-x-1 t-text-red ">
                            <CircleWarning size={11} />
                            <span className="t-text-body-sm t-m-0 t-text-text-30">
                              Overdue
                            </span>
                          </div>
                        )}
                    </Slider.Title>
                    <Slider.Close />
                  </Slider.Header>
                </DashboardContainer.Header>
                <DashboardContainer.Content className="t-h-full t-relative t-top-0">
                  <Tab.Content value="DETAILS" asChild>
                    {data && (
                      <>
                        <Slider.Body>
                          <FilingDetails data={data} />
                          <FilingTransactions
                            currentFilingId={selectedFilling}
                          />
                        </Slider.Body>
                      </>
                    )}
                  </Tab.Content>
                  <Tab.Content value="CHAT" asChild>
                    <div className="t-h-full t-w-full t-relative">
                      <ChatPane
                        setChannelId={setChannelId}
                        channelId={channelId}
                      />
                    </div>
                  </Tab.Content>
                </DashboardContainer.Content>
              </DashboardContainer>
            </Tab.Root>

            <Slider.Footer className="t-sticky t-bottom-0">
              <div className=" t-flex t-flex- t-gap-x-2 t-justify-end ">
                {/* Add sales (bulk invoice upload) — commented out for now
                {Boolean(data?.status === ONGOING_PERIOD) && (
                  <Button
                    size="small"
                    customType="primary"
                    onClick={onAddTransaction}
                  >
                    Add sales
                  </Button>
                )}
                */}

                {data?.task && (
                  <Button
                    customType="secondary"
                    size="small"
                    onClick={handleViewStatus}
                  >
                    View status
                  </Button>
                )}

                {!data?.task && data?.is_in_cart && (
                  <Button
                    customType="secondary"
                    size="small"
                    onClick={handleViewCart}
                  >
                    View cart
                  </Button>
                )}

                {!data?.task &&
                  !data?.is_in_cart &&
                  data?.status === SUBSCRIBED && (
                    <ConditionalToolTip condition="This filing will start automatically once the subscription is billed.">
                      <span>
                        <Button customType="primary" size="small" disabled>
                          Start filing
                        </Button>
                      </span>
                    </ConditionalToolTip>
                  )}

                {!data?.task &&
                  !data?.is_in_cart &&
                  data?.status !== SUBSCRIBED && (
                    <Button
                      customType="primary"
                      size="small"
                      isLoading={isAddingToCart}
                      disabled={isAddingToCart}
                      onClick={() => void startSubscriptionCheckout()}
                    >
                      Start filing
                    </Button>
                  )}
              </div>
            </Slider.Footer>
          </Async.Success>
        </Async.Root>
      </Slider.Content>
    </Slider.Root>
  );
};
